import type { LucideIcon } from 'lucide-react'
import type { ReactNode } from 'react'
import { cn } from '../lib/cn'
import type { Tone } from './types'

export interface BadgeProps {
  tone?: Tone | 'accent'
  /** Standard 'subtle' */
  variant?: 'subtle' | 'solid' | 'outline'
  /** 16 / 20 px hoch */
  size?: 'sm' | 'md'
  icon?: LucideIcon
  /** Pillenform – nur für Zähler */
  pill?: boolean
  /** „Neu“, „3“, „Aktion“, „über KI-Assistent“ */
  children: ReactNode
  className?: string
  'data-testid'?: string
}

export const TONE_SUBTLE: Record<Tone | 'accent', string> = {
  neutral: 'border-line bg-sunken text-fg-muted',
  info: 'border-info-line bg-info-subtle text-info-fg',
  success: 'border-success-line bg-success-subtle text-success-fg',
  warning: 'border-warning-line bg-warning-subtle text-warning-fg',
  danger: 'border-danger-line bg-danger-subtle text-danger-fg',
  accent: 'border-accent-line bg-accent-subtle text-fg-accent',
}
const TONE_SOLID: Record<Tone | 'accent', string> = {
  neutral: 'border-transparent bg-inverse text-fg-inverse',
  info: 'border-transparent bg-info text-on-status',
  success: 'border-transparent bg-success text-on-status',
  warning: 'border-transparent bg-warning text-on-warning',
  danger: 'border-transparent bg-danger text-on-status',
  accent: 'border-transparent bg-accent text-on-accent',
}
const TONE_OUTLINE: Record<Tone | 'accent', string> = {
  neutral: 'border-line-strong text-fg-muted',
  info: 'border-info-line text-info-fg',
  success: 'border-success-line text-success-fg',
  warning: 'border-warning-line text-warning-fg',
  danger: 'border-danger-line text-danger-fg',
  accent: 'border-accent-line text-fg-accent',
}
export const TONE_DOT: Record<Tone | 'accent', string> = {
  neutral: 'bg-fg-subtle',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
  accent: 'bg-accent',
}
export const TONE_TEXT: Record<Tone, string> = {
  neutral: 'text-fg-muted',
  info: 'text-info-fg',
  success: 'text-success-fg',
  warning: 'text-warning-fg',
  danger: 'text-danger-fg',
}
export const TONE_LINE: Record<Tone, string> = {
  neutral: 'bg-line-strong',
  info: 'bg-info',
  success: 'bg-success',
  warning: 'bg-warning',
  danger: 'bg-danger',
}

/** Kleine Marke: „Neu“, Zähler, „über KI-Assistent“. Für Zustände aus dem Fachkern: StatusPill. */
export function Badge({
  tone = 'neutral',
  variant = 'subtle',
  size = 'md',
  icon: Icon,
  pill,
  children,
  className,
  'data-testid': testId,
}: BadgeProps) {
  const tones = variant === 'solid' ? TONE_SOLID : variant === 'outline' ? TONE_OUTLINE : TONE_SUBTLE
  return (
    <span
      data-ui="badge"
      data-testid={testId}
      className={cn(
        'inline-flex shrink-0 items-center gap-1 border font-medium whitespace-nowrap',
        size === 'sm' ? 'h-4 px-1 text-micro tracking-normal' : 'h-5 px-1.5 text-caption',
        pill ? 'min-w-5 justify-center rounded-full tabular-nums' : 'rounded-xs',
        pill && size === 'sm' && 'min-w-4',
        tones[tone],
        className,
      )}
    >
      {Icon && <Icon className="size-3 shrink-0" strokeWidth={2} aria-hidden />}
      {children}
    </span>
  )
}
